class ProductPriceCorrector extends HTMLElement {
  constructor () {
    super()
    this.API_CONFIG = {
      endpoint: `https://${window.Shopify.shop}/api/2025-07/graphql.json`,
      accessToken:
        window.Shopify.shop === 'holland-cooper-us.myshopify.com'
          ? '4b30dc1c20ff119f17b4c3a75a433ad0'
          : 'b03aa3be758de5476316ebef2ea64729'
    }

    // Get current market context from Shopify
    this.country = window.Shopify?.country ? window.Shopify.country.toUpperCase() : null
    this.language = window.Shopify?.locale
      ? window.Shopify.locale.toUpperCase().split('-')[0]
      : null
  }

  connectedCallback () {
    // Find the price element within this component
    const priceElement = this.querySelector('.prd-Price_Current[data-product-handle]')

    if (priceElement) {
      this.correctPrice(priceElement)
    } else {
      this.waitForPriceElement()
    }
  }

  waitForPriceElement () {
    // Use MutationObserver to detect when Nosto adds the price element
    const observer = new MutationObserver(() => {
      const priceElement = this.querySelector('.prd-Price_Current[data-product-handle]')
      if (priceElement) {
        this.correctPrice(priceElement)
        observer.disconnect()
      }
    })

    observer.observe(this, {
      childList: true,
      subtree: true
    })
  }

  async correctPrice (priceElement) {
    const handle = priceElement.getAttribute('data-product-handle')

    if (!handle) {
      console.warn('No product handle found on price element')
      return
    }

    try {
      const price = await this.getProductPrice(handle, priceElement.textContent)
      if (price) {
        priceElement.textContent = price
      }
    } catch (error) {
      console.error(`Failed to update price for ${handle}:`, error)
    }
  }

  async getProductPrice (handle, originalPriceText) {
    let contextDirective = ''
    if (this.country && this.language) {
      contextDirective = `@inContext(country: ${this.country}, language: ${this.language})`
    } else if (this.country) {
      contextDirective = `@inContext(country: ${this.country})`
    }

    const query = `
      query getProduct($handle: String!) ${contextDirective} {
        product(handle: $handle) {
          priceRange {
            minVariantPrice {
              amount
              currencyCode
            }
          }
        }
      }
    `

    const response = await fetch(this.API_CONFIG.endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': this.API_CONFIG.accessToken
      },
      body: JSON.stringify({
        query,
        variables: { handle }
      })
    })

    const result = await response.json()

    if (result.errors) {
      console.error('GraphQL errors:', result.errors)
      return null
    }

    if (result.data?.product?.priceRange?.minVariantPrice) {
      const { amount, currencyCode } = result.data.product.priceRange.minVariantPrice
      return this.formatPrice(amount, currencyCode, originalPriceText)
    }

    return null
  }

  formatPrice (amount, currencyCode, originalPriceText) {
    const numAmount = parseFloat(amount)

    // Extract currency symbol from original price text
    const currencySymbol = originalPriceText.replace(/[\d.,\s]/g, '').trim()

    // Format the number with 2 decimal places
    const formattedAmount = numAmount.toFixed(2)

    // If we found a currency symbol, use it in the same position as original
    if (currencySymbol) {
      // Check if symbol was at the start or end of original price
      const symbolAtStart = originalPriceText.trim().indexOf(currencySymbol) === 0

      if (symbolAtStart) {
        return `${currencySymbol}${formattedAmount}`
      } else {
        return `${formattedAmount}${currencySymbol}`
      }
    }

    // Fallback to Intl formatting if no symbol found
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })

    return formatter.format(numAmount)
  }
}

// Register the custom element
customElements.define('product-price-corrector', ProductPriceCorrector)
